import { Box, Button, Divider, Flex, Input } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Icons, Typography } from "@reconvert/react-ui-component";
import React, { useEffect, useMemo, useState } from "react";
import ConditionSection from "./ConditionSection";
import OrSeprator from "./OrSeprator";
import AndSeprator from "./AndSeprator";

import useAndOrGrouping, { ConditionType } from "../../../../hooks/useAndOrGrouping";
import { useLocation } from "react-router-dom";
import { Platform } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../../../../config";
import { selectAppState } from "../../../../app/slice/appSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Overview, selectConditionsState } from "../../../../app/slice/conditionSlice";
import { startConditionCreateOrUpdate, stopConditionCreateOrUpdate } from "../../../../app/slice/unsavedSlice";
import useConditions from "../../../../hooks/useConditions";
import { BOTTOM_SHEET_TYPES, selectBottomSheet } from "../../../../app/slice/mobileSlice";

const AndOrContainer = () => {
  const {
    conditionData,
    handleOrCondition,
    handleAndCondition,
    handleDeleteCondition,
    updateConditionData,
    handleOnSubmit,
    updateConditionName,
    canSave,
    newlyCreatedSubConditionId,
    conditionNameError,
  } = useAndOrGrouping();

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isAdmin = queryParams.get("isAdmin");
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const { isUnsavedBarEnabled, isMobile } = useAppSelector(selectAppState);
  const bottomSheet = useAppSelector(selectBottomSheet);
  const { selectedCondition } = useConditions();

  const { overview, updatingConditions } = useAppSelector(selectConditionsState);

  const [isHover, setIsHover] = useState(false);
  const [conditionName, setConditionName] = useState(conditionData?.name || "");
  const [preserveName, setPreserveName] = useState(conditionData?.name || "");

  const dispatch = useAppDispatch();

  useEffect(() => {
    setConditionName(t(conditionData?.name || ""));
    setPreserveName(conditionData?.name);
  }, [conditionData?.name]);

  useEffect(() => {
    if (!isUnsavedBarEnabled) return;

    if (
      overview === Overview.ADD_CONDITION_VIEW ||
      bottomSheet.type === BOTTOM_SHEET_TYPES.ADD_CONDITION ||
      (canSave && selectedCondition?.id && (overview === Overview.EDIT_CONDITION_VIEW || bottomSheet.type === BOTTOM_SHEET_TYPES.EDIT_CONDITION))
    ) {
      dispatch(startConditionCreateOrUpdate());
    } else {
      dispatch(stopConditionCreateOrUpdate());
    }
  }, [canSave, dispatch, isUnsavedBarEnabled, overview, selectedCondition?.id, bottomSheet.type]);

  const handleConditionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setPreserveName(e.target.value);
    }

    setConditionName(e.target.value);
  };

  const handleConditionNameBlur = () => {
    setIsEdit(false);
    setIsHover(false);

    if (!conditionName?.trim()) {
      setConditionName(preserveName);

      return;
    }

    updateConditionName(conditionName);
  };

  return (
    <>
      
        <Box>
          <Box my={4}>
            <Box>
              {isEdit ? (
                <Flex gap={1} flexDirection={"column"}>
                  <Typography
                    color={"#6A7381"}
                    width={"max-content"}
                    bg={"var(--Natural-100, #F9FAFC)"}
                    variant="small"
                  >
                    {t("Condition name")}
                  </Typography>
                  <Input
                    value={conditionName}
                    onChange={handleConditionNameChange}
                    _hover={{
                      paddingLeft: "12px",
                    }}
                    onBlur={handleConditionNameBlur}
                    isInvalid={!!conditionNameError}
                    borderColor={conditionNameError ? "red.500" : undefined}
                    _focusVisible={{
                      borderColor: conditionNameError ? "red.500" : undefined,
                      boxShadow: conditionNameError ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined,
                    }}
                  />
                  {conditionNameError && (
                    <Typography variant="small" color="red.500" mt={1}>
                      {conditionNameError}
                    </Typography>
                  )}
                </Flex>
              ) : (
                <Flex
                  onMouseEnter={() => setIsHover(true)}
                  onMouseLeave={() => setIsHover(false)}
                  justifyContent={"space-between"}
                >
                  <Typography
                    textAlign={"left"}
                    variant="subheading1"
                    fontSize={"16px"}
                    color={"neutrals.800"}
                    whiteSpace={"nowrap"}
                    textOverflow={"ellipsis"}
                    overflow={"hidden"}
                  >
                    {conditionName}
                  </Typography>

                  {isHover && (
                    <Icons.PencilSimple
                      onClick={() => setIsEdit(true)}
                      cursor={"pointer"}
                      color={"neutrals.800"}
                      width={"16px"}
                      height={"16px"}
                    />
                  )}
                </Flex>
              )}
            </Box>
          </Box>

          {Array.isArray(conditionData?.any) &&
            conditionData?.any.map((condition, index) => (
              <React.Fragment key={condition.id}>
                {index > 0 && condition?.conditionType === ConditionType.OR && <OrSeprator />}
                {index > 0 && condition?.conditionType === ConditionType.AND && <AndSeprator />}
                <ConditionSection
                  condition={condition}
                  handleAndCondition={handleAndCondition}
                  handleDeleteCondition={handleDeleteCondition}
                  updateConditionData={updateConditionData}
                  allCondition={conditionData?.any}
                  newlyCreatedSubConditionId={newlyCreatedSubConditionId}
                  conditionName={conditionData?.name}
                  updateConditionName={updateConditionName}
                />
              </React.Fragment>
            ))}
          <Divider my={4} />
          <Flex
            gap={2}
            mt={4}
            mb={"50px"}
            cursor={"pointer"}
            width={"fit-content"}
            onClick={handleOrCondition}
            justifyContent="center"
            align="center"
            border="1px solid #363C45"
            height="min-content"
            borderRadius="16px"
            px="8px"
            py="4px"
            _hover={{ bg: "#F0F3F7" }}
          >
            <Typography variant="xsmall">{t("OR")}</Typography>
            <Icons.Plus cursor="pointer" height={"12px"} width={"12px"} />
          </Flex>
        </Box>
      

      {(isAdmin || APP_PLATFORM === Platform.WIX ) && (
        <>
          <Button
            mt={4}
            mb={8}
            onClick={handleOnSubmit}
            width={"100%"}
            isLoading={updatingConditions}
            isDisabled={!canSave}
          >
            {t("Save")}
          </Button>
          <Divider />
        </>
      )}
    </>
  );
};

export default AndOrContainer;
